import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { SCORING_PRESETS, scoreRound } from '@shared/game-logic';
import { Panel, Screen } from '@/components/ui';
import { SCORING_PRESET_LABELS, signed } from '@/lib/labels';

/**
 * The rules, written to be read at the table by someone who has never played.
 * The in-app hints handle the moment-to-moment "why can't I bid that?"; this
 * page is the whole picture.
 */
export default function Rules() {
  return (
    <Screen title="Spelregels" back="/" subtitle="Boerenbridge, van delen tot punten">
      <Panel title="Een ander spel">
        <Prose>
          <p>
            Zoek je de regels van <strong>hartenjagen</strong>?{' '}
            <Link to="/regels/hartenjagen" className="text-accent-400 underline underline-offset-4">
              Die staan hier
            </Link>
            . Dat spel deelt alleen het kaartspel met boerenbridge — daar wil je juist zo min
            mogelijk punten. En de regels van{' '}
            <Link
              to="/regels/klaverjassen"
              className="text-accent-400 underline underline-offset-4"
            >
              klaverjassen
            </Link>{' '}
            staan er ook.
          </p>
        </Prose>
      </Panel>

      <Panel title="Waar het om gaat">
        <Prose>
          <p>
            Boerenbridge draait om één ding:{' '}
            <strong>voorspellen hoeveel slagen je gaat halen</strong> — en dat dan precies
            waarmaken. Niet meer, niet minder. Eén slag te veel is net zo erg als één te weinig.
          </p>
          <p>
            Je speelt het met 3 tot 7 spelers en een gewoon pak van 52 kaarten. Een potje bestaat
            uit een reeks rondes waarin het aantal kaarten steeds verandert.
          </p>
        </Prose>
      </Panel>

      <Panel title="1 · Delen en troef">
        <Prose>
          <p>
            Elke ronde deelt iemand anders — het delen schuift telkens één plek op. In de eerste
            ronde krijgt iedereen <strong>één kaart</strong>, in de tweede ronde twee, enzovoort.
          </p>
          <p>
            Na het delen draait de deler de bovenste kaart van de reststapel om. De kleur van die
            kaart is de <strong>troef</strong> voor deze ronde. Troef slaat elke andere kleur.
          </p>
          <p>
            In de grootste ronde kan de stapel precies op zijn. Dan is er geen troefkaart en speel
            je de ronde <strong>troefloos</strong>: alleen de gevraagde kleur telt.
          </p>
          <p>
            Daarnaast wordt er standaard nog <strong>één extra ronde</strong> gespeeld met evenveel
            kaarten als de grootste, maar dan bewust zonder troef. Dat is meteen de moeilijkste
            ronde van het potje: je kunt niets meer afstoppen met een troefje.
          </p>
          <p>
            Er is ook een variant waarin de hele <strong>terugweg troefloos</strong> is: omhoog
            speel je met troef, en vanaf de top gaat alles zonder. De tweede helft van het potje
            wordt daarmee een ander spel — je hoge kaarten in de lange kleuren zijn dan pas echt wat
            waard. Aan te zetten bij Rondes.
          </p>
        </Prose>
      </Panel>

      <Panel title="De blinde ronde">
        <Prose>
          <p>
            In een ronde met <strong>één kaart</strong> houd je die kaart niet naar je toe, maar
            naar buiten — als een vlaggetje op je voorhoofd. Iedereen ziet dus jouw kaart, en jij
            ziet die van alle anderen. Alleen je eigen kaart blijft een raadsel.
          </p>
          <Callout>
            Je biedt op wat je bij de anderen ziet liggen, niet op wat je zelf hebt.
          </Callout>
          <p>
            Zie je bij iedereen een hoge kaart, dan is die ene onbekende kaart van jou
            waarschijnlijk niet de winnaar — dus bied 0. Zie je overal lage kaartjes, dan maak jij
            misschien juist wel een kans.
          </p>
          <p className="text-slate-400">
            In het live spel regelt de app dit: je eigen kaart ligt op de achterkant en je speelt
            hem met één knop, zonder te weten wat het is. Met echte kaarten houd je hem gewoon
            omgedraaid vast. Aan te zetten en uit te zetten bij de huisregels.
          </p>
        </Prose>
      </Panel>

      <Panel title="2 · Bieden">
        <Prose>
          <p>
            Iedereen kijkt naar zijn kaarten en zegt hardop hoeveel slagen hij denkt te halen. Je
            mag ook <strong>0</strong> bieden — dat is vaak een prima bod, en zeker niet lafhartig.
          </p>
          <p>
            Het bieden begint bij de speler <strong>links van de deler</strong> en gaat de tafel
            rond. De deler is dus als laatste aan de beurt, en die heeft één beperking:
          </p>
          <Callout>
            De deler mag níet zó bieden dat alle biedingen samen precies uitkomen op het aantal
            slagen.
          </Callout>
          <p>
            Waarom? Als de biedingen precies kloppen, zou in theorie iedereen zijn bod kunnen halen
            en gaat niemand onderuit. Door dat te verbieden zit er altijd minstens één speler fout.
            Dat is precies de spanning van het spel — en meteen het lastigste moment voor de deler.
          </p>
          <p className="text-slate-400">
            In een ronde van 5 slagen waarin de anderen samen 3 hebben geboden, mag de deler dus
            geen 2 zeggen. Alle andere getallen mogen wel. De app zet dat getal vanzelf uit.
          </p>
        </Prose>
      </Panel>

      <Panel title="3 · De slagen spelen">
        <Prose>
          <p>
            De speler links van de deler komt uit met een kaart naar keuze. Daarna speelt iedereen
            om de beurt één kaart, met één harde regel:
          </p>
          <Callout>
            Kun je de gevraagde kleur volgen, dan <strong>moet</strong> je dat doen.
          </Callout>
          <p>
            Heb je die kleur niet, dan mag je alles spelen: troeven om de slag te pakken, of een
            waardeloze kaart weggooien. Aan sommige tafels geldt dan <strong>troefplicht</strong> —
            dan móet je troeven als je troef hebt. Spreek dat vooraf af; in de app is het een
            schakelaar.
          </p>
          <p>
            De slag gaat naar de <strong>hoogste troef</strong>. Ligt er geen troef, dan naar de
            hoogste kaart van de kleur die werd gevraagd. Aas is de hoogste kaart, 2 de laagste. Wie
            de slag pakt, komt uit voor de volgende.
          </p>
          <p className="text-slate-400">
            Een kaart van een andere kleur dan troef of de gevraagde kleur kan de slag nooit winnen.
            Weggooien is dus altijd veilig — als je maar niet kon volgen.
          </p>
        </Prose>
      </Panel>

      <Panel title="4 · Punten">
        <Prose>
          <p>
            Aan het eind van de ronde telt iedereen zijn slagen. Haal je <strong>precies</strong> je
            bod, dan krijg je een vaste bonus plus een beloning per geboden slag. Zit je ernaast —
            te veel óf te weinig — dan verlies je punten per slag verschil.
          </p>
        </Prose>

        <div className="mt-3 space-y-3">
          {(['nl-standaard', 'dubbel-bod', 'kwadraat', 'mild'] as const).map((preset) => {
            const config = SCORING_PRESETS[preset];
            const squared = config.bidSquaredBonus ?? 0;
            return (
              <div key={preset} className="rounded-xl bg-base-900/60 p-3">
                <p className="font-medium">{SCORING_PRESET_LABELS[preset]}</p>
                <p className="mt-0.5 text-sm text-slate-400">
                  Precies goed: {config.exactBase}
                  {config.perTrickBid === 1 && ' + je bod'}
                  {config.perTrickBid > 1 && ` + ${config.perTrickBid} × je bod`}
                  {squared > 0 &&
                    (squared === 1
                      ? ' + je bod in het kwadraat'
                      : ` + ${squared} × je bod in het kwadraat`)}
                  . Ernaast: {config.missPenaltyPerTrick} strafpunt
                  {config.missPenaltyPerTrick === 1 ? '' : 'en'} per slag verschil.
                </p>
                <p className="mt-2 font-tabular text-sm text-slate-300">
                  Bod 3, gehaald 3 → <strong>{signed(scoreRound(3, 3, config))}</strong> · bod 3,
                  gehaald 4 → <strong>{signed(scoreRound(3, 4, config))}</strong> · bod 0, gehaald 0
                  → <strong>{signed(scoreRound(0, 0, config))}</strong>
                </p>
                {squared > 0 && (
                  <p className="mt-1 text-sm text-slate-400">
                    Hier loont een groot bod echt: 2 halen is {signed(scoreRound(2, 2, config))},
                    maar 4 halen al {signed(scoreRound(4, 4, config))} en 6 halen{' '}
                    {signed(scoreRound(6, 6, config))}. De straf blijft wel gewoon{' '}
                    {config.missPenaltyPerTrick} per slag, dus mis je het, dan mis je het hard.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <Prose>
          <p className="mt-3">
            Merk op dat 0 bieden en 0 halen gewoon punten oplevert. Een ronde waarin je niets wilt,
            maar toch één slag binnenkrijgt, kost je juist punten. Ongewild een slag pakken is in
            dit spel een echte ramp.
          </p>
        </Prose>
      </Panel>

      <Panel title="5 · Het verloop van een potje">
        <Prose>
          <p>
            De rondes lopen <strong>op en neer</strong>: van 1 kaart omhoog tot de grootste ronde en
            dan weer terug naar 1. Met 4 spelers en een maximum van 7 zijn dat 13 rondes.
          </p>
          <p>
            Hoe groot de grootste ronde mag zijn hangt af van het aantal spelers — de 52 kaarten
            moeten wel rond. Met 4 spelers kan 13, met 7 spelers nog 7. In de app stel je zelf een
            maximum in; 7 is een prettige lengte voor een avond.
          </p>
          <p>
            Wil je het hele pak gebruiken, dan zet je het maximum met één tik op de deck-grens. Bij
            4 spelers wordt dat 13 kaarten en dus een flink langer potje — de app laat vooraf zien
            hoeveel rondes en hoeveel tijd dat ongeveer kost.
          </p>
          <p>
            Na de laatste ronde wint wie de meeste punten heeft. Negatief eindigen kan, en gebeurt.
          </p>
        </Prose>
      </Panel>

      <Panel title="Veelgemaakte fouten">
        <Prose>
          <ul className="list-disc space-y-1.5 pl-5">
            <li>
              <strong>Vergeten dat te veel ook fout is.</strong> Een extra slag pakken omdat het
              kan, terwijl je bod al binnen is, kost punten.
            </li>
            <li>
              <strong>Te braaf 0 bieden met een aas.</strong> Een aas in een korte kleur pakt bijna
              altijd een slag, of je wilt of niet.
            </li>
            <li>
              <strong>Niet volgen terwijl het wel kan.</strong> Volgen is verplicht — controleer je
              hand voordat je troeft.
            </li>
            <li>
              <strong>De deler laten passen.</strong> De deler moet altijd een getal noemen; alleen
              dat ene passende getal is verboden.
            </li>
            <li>
              <strong>Slagen verkeerd optellen.</strong> Alle slagen samen zijn precies het aantal
              kaarten van die ronde. De app laat je pas verder als het klopt.
            </li>
          </ul>
        </Prose>
      </Panel>

      <Panel title="Tips om te beginnen">
        <Prose>
          <ul className="list-disc space-y-1.5 pl-5">
            <li>
              Tel je azen en heren, en kijk hoeveel troef je hebt. Dat is meestal je bod, min een
              beetje.
            </li>
            <li>
              In een ronde van 1 kaart is 0 of 1 bieden bijna gokken. Kijk naar de troefkaart die
              openligt.
            </li>
            <li>
              Wil je een slag kwijt? Speel je hoge kaarten weg zolang anderen nog troef hebben.
            </li>
            <li>
              Let op wat anderen boden. Zit de tafel samen boven het aantal slagen, dan gaat er
              zeker iemand nat — misschien jij.
            </li>
          </ul>
        </Prose>
      </Panel>
    </Screen>
  );
}

function Prose({ children }: { children: ReactNode }) {
  return <div className="space-y-3 text-sm leading-relaxed text-slate-300">{children}</div>;
}

function Callout({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl border-l-2 border-accent-500 bg-accent-500/10 px-3 py-2 text-slate-200">
      {children}
    </p>
  );
}
